<template>
  <b-card class="mb-3">
    <template #header>
      <div>
        <i class="fa fa-ellipsis-v mr-2 drag-handle"></i>
        <i class="icon-user"></i> <strong>General Information</strong>
      </div>
    </template>

    <b-form>
      <b-row>
        <b-col md="6">
          <b-form-group label="Username" label-for="username-component">
            <username
              id="username-component"
              :allowed="!hasPermission(UserNameChange)"
              :userid="userData.id"
              :user="userData.username"
            />
          </b-form-group>
        </b-col>
        <b-col md="6" v-if="authUser.security === 1 || authUser.security === 5">
          <b-form-group label="Role">
            <role :user="userData.username" :current-role="userData.security_id" :key="userData.id"></role>
          </b-form-group>
        </b-col>
      </b-row>

      <b-row>
        <b-col md="6">
          <b-form-group label="Email" label-for="email-component">
            <email
              id="email-component"
              :readonly="servers[userData.server].readonly || !hasPermission(UserIPsEmailView)"
              :allowed="!hasPermission(UserEmailChange)"
              :username="userData.username"
              :email="userData.email"
            />
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group label="Discord Tag">
            <b-input-group>
              <b-input-group-prepend is-text><i class="fa fa-hashtag"></i></b-input-group-prepend>
              <b-form-input readonly :value="userData.discord_tag" />
            </b-input-group>
          </b-form-group>
        </b-col>
      </b-row>

      <b-row>
        <b-col :md="hasGuild ? 4 : 6">
          <b-form-group label="Guild">
            <b-input-group>
              <b-input-group-prepend is-text><i class="fa fa-users"></i></b-input-group-prepend>
              <b-form-input
                v-model="guildName"
                :readonly="!canEditGuildName"
                :state="canEditGuildName ? guildNameState : null"
              />
              <b-input-group-append v-if="canEditGuildName">
                <b-button
                  variant="primary"
                  :disabled="userLock || !guildNameState || !guildNameDirty"
                  @click="changeGuildName"
                >
                  Change
                </b-button>
              </b-input-group-append>
            </b-input-group>
            <b-form-invalid-feedback v-if="canEditGuildName" :state="guildNameState">
              Use alphanumeric characters and spaces only, max 15 characters.
            </b-form-invalid-feedback>
          </b-form-group>
        </b-col>
        <b-col md="4" v-if="hasGuild">
          <b-form-group label="Guild Role">
            <b-input-group>
              <b-input-group-prepend is-text><i class="fa fa-shield"></i></b-input-group-prepend>
              <b-form-select
                v-if="canEditGuildRole"
                v-model="guildRole"
                :options="guildRoleOptions"
                :disabled="userLock"
              />
              <b-form-input v-else readonly :value="userData.guild_role" />
              <b-input-group-append v-if="canEditGuildRole">
                <b-button
                  variant="primary"
                  :disabled="userLock || !guildRoleDirty"
                  @click="changeGuildRole"
                >
                  Change
                </b-button>
              </b-input-group-append>
            </b-input-group>
          </b-form-group>
        </b-col>
        <b-col :md="hasGuild ? 4 : 6">
          <b-form-group label="Membership Expiry">
            <b-input-group>
              <b-input-group-prepend is-text><i class="fa fa-star"></i></b-input-group-prepend>
              <b-form-input
                readonly
                :value="formatDateDisplay(userData.member_expire)"
                v-b-tooltip.hover.html="getDateTooltip(userData.member_expire)"
              />
              <b-input-group-append v-if="canViewMembershipExtension">
                <b-dropdown
                  right
                  no-caret
                  variant="outline-secondary"
                  :disabled="userLock || serverReadOnly"
                >
                  <template #button-content>
                    <i class="fa fa-chevron-down"></i>
                  </template>
                  <b-dropdown-item :disabled="!canExtendMembership" @click="extendMembership(3)">Extend 3 days</b-dropdown-item>
                  <b-dropdown-item :disabled="!canExtendMembership" @click="extendMembership(7)">Extend 7 days</b-dropdown-item>
                </b-dropdown>
              </b-input-group-append>
            </b-input-group>
          </b-form-group>
        </b-col>
      </b-row>

      <b-row>
        <b-col md="6">
          <b-form-group label="Money">
            <money
              :allowed="!hasPermission(UserMoneyChange)"
              :userid="userData.id"
              :price="userData.money"
              :server="userData.server"
            />
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group label="Coins">
            <coins
              :allowed="!hasPermission(UserCoinChange)"
              :userid="userData.id"
              :coins="userData.coins"
              :server="userData.server"
            />
          </b-form-group>
        </b-col>
      </b-row>

      <b-row>
        <b-col md="4">
          <b-form-group label="Playtime">
            <b-input-group>
              <b-input-group-prepend is-text><i class="fa fa-gamepad"></i></b-input-group-prepend>
              <b-form-input readonly :value="convertMinsToHrsMins(userData.playtime)" />
            </b-input-group>
          </b-form-group>
        </b-col>
        <b-col md="4">
          <b-form-group label="Registered">
            <b-input-group>
              <b-input-group-prepend is-text><i class="fa fa-calendar"></i></b-input-group-prepend>
              <b-form-input
                readonly
                :value="formatDateDisplay(userData.reg_date)"
                v-b-tooltip.hover.html="getDateTooltip(userData.reg_date)"
              />
            </b-input-group>
          </b-form-group>
        </b-col>

        <b-col md="4">
          <b-form-group label="Last Online">
            <b-input-group>
              <b-input-group-prepend is-text><i class="fa fa-clock-o"></i></b-input-group-prepend>
              <b-form-input
                readonly
                :value="formatDateDisplay(userData.last_active)"
                v-b-tooltip.hover.html="getDateTooltip(userData.last_active)"
              />
            </b-input-group>
          </b-form-group>
        </b-col>
      </b-row>
    </b-form>
  </b-card>
</template>

<script>
import Username from '../Username'
import Email from '../Email'
import Coins from '../Coins'
import Money from '../Money'
import Role from '../Role'
import moment from 'moment'
import { mapActions } from 'vuex'

export default {
  name: 'GeneralInfoCard',
  components: {
    Username,
    Email,
    Coins,
    Money,
    Role
  },
  props: {
    userData: { type: Object, required: true },
    authUser: { type: Object, required: true },
    servers: { type: Object, required: true },
    userLock: { type: Boolean, default: false }
  },
  data () {
    return {
      guildName: '',
      guildRole: '',
      guildRoleOptions: [
        { value: 'Initiate', text: 'Initiate' },
        { value: 'Member', text: 'Member' },
        { value: 'Officer', text: 'Officer' },
        { value: 'Leader', text: 'Leader' }
      ]
    }
  },
  computed: {
    hasGuild () {
      return Number(this.userData.guild_id || 0) > 0
    },
    isUserOnline () {
      return !!(this.userData && this.userData.online)
    },
    serverReadOnly () {
      return !!(this.userData && this.servers[this.userData.server] && this.servers[this.userData.server].readonly)
    },
    canEditGuildRole () {
      return this.hasGuild && !this.isUserOnline && this.userData.guild_role !== 'Leader' && !this.serverReadOnly && this.hasPermission('UserGuildRoleChange')
    },
    canEditGuildName () {
      return this.hasGuild && this.userData.guild_role === 'Leader' && !this.serverReadOnly && this.hasPermission('UserGuildNameChange')
    },
    canViewMembershipExtension () {
      return !this.serverReadOnly && this.hasPermission('UserMembershipExtend')
    },
    canExtendMembership () {
      return this.canViewMembershipExtension && !this.isUserOnline
    },
    guildNameState () {
      const name = this.guildName || ''
      return name.trim().length > 0 && name.length <= 15 && /^[A-Za-z0-9 ]+$/.test(name)
    },
    guildNameDirty () {
      return (this.guildName || '').trim() !== (this.userData.guild || '').trim()
    },
    guildRoleDirty () {
      return this.guildRole && this.guildRole !== this.userData.guild_role
    }
  },
  watch: {
    userData: {
      immediate: true,
      handler (val) {
        this.guildName = val && val.guild ? val.guild : ''
        this.guildRole = val && val.guild_role ? val.guild_role : ''
      }
    }
  },
  methods: {
    ...mapActions(['setLock']),
    changeGuildRole () {
      if (!this.canEditGuildRole || !this.guildRoleDirty) return
      this.setLock(true)
      this.emit('changeGuildRole', {
        id: this.userData.id,
        value: this.guildRole,
        server: this.userData.server
      })
    },
    changeGuildName () {
      if (!this.canEditGuildName || !this.guildNameState || !this.guildNameDirty) return
      this.setLock(true)
      this.emit('changeGuildName', {
        id: this.userData.id,
        guild_id: this.userData.guild_id,
        name: this.guildName.trim(),
        server: this.userData.server
      })
    },
    async extendMembership (days) {
      if (!this.canExtendMembership || (days !== 3 && days !== 7)) return
      const confirmed = await this.$bvModal.msgBoxConfirm(
        `Extend membership for ${this.userData.username} by ${days} days?`,
        {
          title: 'Confirm Membership Extension',
          okTitle: `Extend ${days} days`,
          cancelTitle: 'Cancel',
          centered: true,
          headerBgVariant: 'dark',
          headerTextVariant: 'light',
          bodyBgVariant: 'dark',
          bodyTextVariant: 'light'
        }
      )
      if (!confirmed) return
      this.setLock(true)
      this.emit('extendMembership', {
        id: this.userData.id,
        value: days,
        server: this.userData.server
      })
    },
    convertMinsToHrsMins (mins) {
      if (!mins) return '0m'
      const h = Math.floor(mins / 60)
      const m = mins % 60
      let res = ''
      if (h > 0) res += `${h}h `
      res += `${m}m`
      return res
    },
    formatDateDisplay(val) {
        if (!val || val.startsWith('0001')) return '-';
        return moment(val).format('YYYY-MM-DD HH:mm:ss');
    },
    getDateTooltip(val) {
        if (!val || val.startsWith('0001')) return '';
        return moment(val).fromNow();
    }
  }
}
</script>
